import { useRequest } from 'features/request/useRequest';
import type { WorkOrderVersionPriceItemFilterInput } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getWorkOrderVariables, getWorkOrderWhereFromFilter } from '../../WorkOrder/WorkOrderTable/helpers';
import { uerWorkOrderVersionPriceItems } from '../workOrderVersionQueries';

type TWorkOrderData = {
    objectId: number | undefined;
    subObjectId: number | undefined;
    constructionTypeId: number;
    versionId?: number;
};

export const useWorkOrderVersionData = ({ objectId, subObjectId, constructionTypeId, versionId }: TWorkOrderData) => {
    const [filtersWhere, setFiltersWhere] = useState<WorkOrderVersionPriceItemFilterInput | undefined>(undefined);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevState) => ({
            ...prevState,
            ...getWorkOrderWhereFromFilter(fieldName, checkedValues),
        }));
    }, []);

    const [data, , , request] = useRequest({
        query: uerWorkOrderVersionPriceItems,
        withEffect: false,
        requestId: 'uerWorkOrderVersionPriceItems',
    });

    const workOrderDataVariables = useMemo<
        | {
              workOrderVersionId: number;
              constructionTypeId: number;
              where?: WorkOrderVersionPriceItemFilterInput;
          }
        | undefined
    >(() => {
        if (constructionTypeId && versionId) {
            return {
                workOrderVersionId: versionId,
                constructionTypeId,
                ...getWorkOrderVariables(objectId, subObjectId, filtersWhere),
            };
        }

        return undefined;
    }, [constructionTypeId, filtersWhere, objectId, subObjectId, versionId]);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'uerWorkOrderVersionPriceItems',
        requestFn: request,
        requestVariables: workOrderDataVariables,
    });

    useEffect(() => {
        if (data?.uerWorkOrderVersionPriceItems?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                data?.uerWorkOrderVersionPriceItems?.items,
                data?.uerWorkOrderVersionPriceItems?.totalCount,
            );
        }
    }, [
        data?.uerWorkOrderVersionPriceItems?.items,
        data?.uerWorkOrderVersionPriceItems?.totalCount,
        getRowParams,
        versionId,
    ]);

    const totalItems = useMemo(
        () => data?.uerWorkOrderVersionPriceItems?.totalCount,
        [data?.uerWorkOrderVersionPriceItems?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    return {
        workOrderPriceData: dataSource,
        fetchedItems,
        totalItems,
        changeFilterHandler,
    };
};
